import { useMemo } from 'react';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useHeadingKeywords } from 'hooks/useHeadingKeywords';
import { buildHeadingLabelSet } from 'domains/reporter/RichTextEditor';
import type { WithApolloContext } from 'modules/Apollo/types';

/**
 * RP-6070 (M2b): the label set the read-only `Renderer` needs to style heading-like
 * paragraphs as headings, gated on `REPORTER_HEADING_NODES_DEPRECATED`. Returns
 * `undefined` when the flag is off, so the Renderer behaves exactly as before.
 * Shared by every read-only report surface (section/addendum/jacket views).
 */
export const useReadOnlyHeadingLabelSet = ({
  context,
}: WithApolloContext): Set<string> | undefined => {
  const [isHeadingNodesDeprecated] = useFeatureFlagEnabled(FF.REPORTER_HEADING_NODES_DEPRECATED);
  // Skip the GET_HEADING_KEYWORDS query when the flag is off so flag-off is truly byte-identical
  // (no extra network request on read-only surfaces that mount this hook).
  const headingKeywords = useHeadingKeywords({ context, skip: !isHeadingNodesDeprecated });
  return useMemo(
    () => (isHeadingNodesDeprecated ? buildHeadingLabelSet(headingKeywords) : undefined),
    [isHeadingNodesDeprecated, headingKeywords]
  );
};
